import React from 'react';
import { Box, Flex, Stack, UseDisclosureReturn, Text, Badge, Tooltip } from '@chakra-ui/react';
import { CaretDoubleLeft, List } from 'phosphor-react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Options from './Options';
import { SidebarDrill } from './SidebarDrill';
import { useChatActive } from '../../hooks/useChatActive';
import { useAuth } from '../../providers/auth';
import { MODULOS_SIDEBAR, ModuloTile, isModuloAtivo } from './modulos';
import { SUBMODULOS_POR_MODULO } from './submodulos';
import { useModuloDrill } from './useModuloDrill';

interface SidebarProps {
  isOpen: boolean;
  onToggle: UseDisclosureReturn['onToggle'];
}

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
  const location = useLocation();
  const isChatActive = useChatActive();
  const { hasPermission, isAdmin } = useAuth();

  // Filtra módulos baseado nas permissões
  const OpenRoutes = MODULOS_SIDEBAR.filter(route => {
    if (isAdmin()) return true;
    return hasPermission(route.path);
  });

  // Drill mode via flag declarativa `hasDrillSidebar` em private.routes (a
  // mesma regra que o cabeçalho usa para mostrar os módulos do sistema).
  const { isDrillMode, drillModule } = useModuloDrill();

  return (
    <Stack
      position="fixed"
      transition="width 0.35s cubic-bezier(0.4, 0, 0.2, 1)"
      bg="rgba(255, 255, 255, 0.96)"
      borderRight="1px solid"
      borderRightColor="rgba(15, 23, 42, 0.06)"
      boxShadow="8px 0 32px -24px rgba(27, 136, 245, 0.35)"
      w={isOpen ? '260px' : '64px'}
      h="100%"
      spacing={0}
      overflowY="auto"
      overflowX="hidden"
      userSelect="none"
      css={{
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#CBD5E0',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#A0AEC0',
        },
      }}
    >
      <Box flexShrink={0} px={isOpen ? 3 : 2} pt={3}>
        {/* Recolher / expandir */}
        <Flex
          as="button"
          type="button"
          onClick={onToggle}
          aria-label={isOpen ? 'Recolher menu' : 'Expandir menu'}
          align="center"
          justify={isOpen ? 'space-between' : 'center'}
          w="full"
          h="40px"
          px={isOpen ? 3 : 0}
          mb={1}
          borderRadius="12px"
          color="#64748B"
          transition="background 0.18s ease, color 0.18s ease"
          _hover={{ bg: '#F1F5F9', color: '#0F172A' }}
          className="hidden md:flex"
        >
          {isOpen ? (
            <>
              <Text
                fontSize="11px"
                fontWeight={700}
                letterSpacing="0.14em"
                textTransform="uppercase"
              >
                Menu
              </Text>
              <CaretDoubleLeft size={16} weight="bold" />
            </>
          ) : (
            <List size={20} weight="bold" />
          )}
        </Flex>

        <Options isOpen={isOpen} />
      </Box>

      {/* Conteudo principal: alterna entre modulos raiz e drill da Conciliacao */}
      <AnimatePresence mode="wait">
        {isDrillMode && drillModule ? (
          <motion.div
            key="drill"
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 30, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            style={{ flexShrink: 0 }}
          >
            <SidebarDrill
              basePath={drillModule.path}
              moduleName={drillModule.name}
              moduleColor={drillModule.bgColor}
              moduleIcon={drillModule.icon}
              isOpen={isOpen}
              backPath={drillModule.backPath}
              backLabel={drillModule.backLabel}
              itens={SUBMODULOS_POR_MODULO[drillModule.path]?.map(sub => ({
                name: sub.title,
                path: sub.path,
              }))}
            />
          </motion.div>
        ) : (
          <motion.div
            key="root"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -20, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
            style={{ flexShrink: 0 }}
          >
            <Box px={isOpen ? 3 : 2} pt={3} pb={24} flexShrink={0}>
              <Box mx={isOpen ? 3 : 1} mb={3} borderTop="1px solid" borderColor="#EEF2F6" />
              {isOpen && (
                <Text
                  px={3}
                  mb={2}
                  fontSize="10.5px"
                  fontWeight={700}
                  letterSpacing="0.14em"
                  textTransform="uppercase"
                  color="#94A3B8"
                >
                  Módulos
                </Text>
              )}
              <Flex direction="column" gap="2px">
                {OpenRoutes.map(item => {
                  const ativo = isModuloAtivo(item.path, location.pathname);
                  const isChatItem = item.path === '/chatPro';
                  const showIndicator = isChatItem && isChatActive;

                  return (
                    <Tooltip
                      key={item.path}
                      label={showIndicator ? 'Você tem conversas em aberto no chat' : item.name}
                      placement="right"
                      isDisabled={isOpen && !showIndicator}
                      hasArrow
                    >
                      <Flex
                        as={RouterLink}
                        to={item.path}
                        aria-current={ativo ? 'page' : undefined}
                        align="center"
                        gap={3}
                        w="full"
                        p="6px"
                        pr={isOpen ? 3 : '6px'}
                        justify={isOpen ? 'flex-start' : 'center'}
                        borderRadius="14px"
                        bg={ativo ? '#EAF4FF' : 'transparent'}
                        cursor="pointer"
                        transition="background 0.18s ease"
                        _hover={{ bg: ativo ? '#E1EFFF' : '#F5F8FB' }}
                        flexShrink={0}
                      >
                        <Box position="relative" flexShrink={0}>
                          <ModuloTile modulo={item} ativo={ativo} />
                          {showIndicator && (
                            <Badge
                              position="absolute"
                              top="0"
                              right="0"
                              colorScheme="red"
                              borderRadius="full"
                              w="10px"
                              h="10px"
                              p={0}
                              border="2px solid white"
                              transform="translate(25%, -25%)"
                            />
                          )}
                        </Box>
                        {isOpen && (
                          <Text
                            flex={1}
                            textAlign="left"
                            fontSize="13px"
                            fontWeight={ativo ? 700 : 500}
                            color={ativo ? '#1B88F5' : '#334155'}
                            lineHeight="1.3"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                          >
                            {item.name}
                          </Text>
                        )}
                      </Flex>
                    </Tooltip>
                  );
                })}
              </Flex>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Stack>
  );
};

export default Sidebar;
